import { cn } from '@/util/cn';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';
import { FC } from 'react';
import { Link, LinkProps } from 'react-router';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'text-muted-foreground inline-flex h-9 items-center justify-center gap-x-2 rounded-lg bg-transparent',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:ring-ring data-[state=active]:bg-primary data-[state=active]:text-primary-foreground bg-secondary text-secondary-foreground inline-flex items-center justify-center rounded-t-xl px-3 py-2 text-sm font-medium whitespace-nowrap transition-all focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow',
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export const TabNavLink: FC<
  LinkProps & { active?: boolean; children: React.ReactNode }
> = ({ active = false, children, to, ...props }) => {
  return (
    <Link
      prefetch="render"
      className={cn(
        'ring-offset-background focus-visible:ring-ring bg-secondary text-secondary-foreground inline-flex items-center justify-center rounded-t-xl px-2 py-1.5 text-xs font-bold whitespace-nowrap transition-all focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 sm:px-3 sm:py-2 sm:text-sm',
        {
          'bg-primary text-primary-foreground shadow': active
        }
      )}
      to={to}
      {...props}
    >
      {children}
    </Link>
  );
};

export const TabNav: FC<{
  children: React.ReactNode;
}> = ({ ...props }) => {
  return (
    <div
      className="text-muted-foreground flex h-auto min-h-[2.25rem] flex-wrap items-center justify-start gap-2 rounded-lg bg-transparent p-1"
      {...props}
    />
  );
};

export { Tabs, TabsList, TabsTrigger, TabsContent };
